import { useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../../contexts/AuthContext";
import { reportService } from "../../../../services/api/reportService";
import { hasChartData } from "../../components/EmptyChart";
import {
    buildPrintHTML,
    buildReportTableSection,
    generateChartImage,
    toBase64FromPublic,
} from "../reportUtils";
import { getBackendUrl } from "../../../../utils/env";
import OverviewTabPresenter from "./OverviewTabPresenter";

const OverviewTab = () => {
    const { token_client } = useContext(AuthContext);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [hoveredCard, setHoveredCard] = useState(null);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [appliedFilters, setAppliedFilters] = useState({ fromDate: "", toDate: "" });

    const [serviceReport, setServiceReport] = useState(null);
    const [profileReport, setProfileReport] = useState(null);
    const [financialReport, setFinancialReport] = useState(null);

    const convertToAPIFormat = (dateStr) => {
        if (!dateStr) return "";
        const [dd, mm, yyyy] = dateStr.split("-");
        if (!dd || !mm || !yyyy) return "";
        return `${dd}/${mm}/${yyyy}`;
    };

    const validateDateRange = () => {
        if (toDate && !fromDate) {
            toast.error("Vui lòng nhập ngày bắt đầu.");
            return false;
        }
        if (fromDate && !toDate) {
            toast.error("Vui lòng nhập ngày kết thúc.");
            return false;
        }
        if (fromDate && toDate) {
            const from = new Date(fromDate.split("-").reverse().join("-"));
            const to = new Date(toDate.split("-").reverse().join("-"));
            if (from > to) {
                toast.error("Ngày bắt đầu không được lớn hơn ngày kết thúc.");
                return false;
            }
        }
        return true;
    };

    const fetchReports = useCallback(async () => {
        try {
            setLoading(true);

            const params = {
                overview: true,
                fromDate: appliedFilters.fromDate ? convertToAPIFormat(appliedFilters.fromDate) : undefined,
                toDate: appliedFilters.toDate ? convertToAPIFormat(appliedFilters.toDate) : undefined,
            };

            const [serviceRes, profileRes, financialRes] = await Promise.all([
                reportService.get_report_client_service(token_client, params),
                reportService.get_report_client_profile(token_client, { ...params, serviceId: "", status: "" }),
                reportService.get_report_client_finance(token_client, params),
            ]);

            setServiceReport(serviceRes);
            setProfileReport(profileRes);
            setFinancialReport(financialRes);
        } catch (err) {
            console.error("Lỗi lấy báo cáo:", err);
            toast.error("Không thể tải báo cáo");
        } finally {
            setLoading(false);
        }
    }, [token_client, appliedFilters]);

    useEffect(() => {
        fetchReports();
    }, [fetchReports]);

    const handleApplyFilter = () => {
        if (!validateDateRange()) return;
        setAppliedFilters({ fromDate, toDate });
    };

    const handleExport = async () => {
        try {
            if (!validateDateRange()) return;

            setExporting(true);

            const serviceChartImg = hasChartData(serviceReport?.chart)
                ? await generateChartImage(serviceReport.chart, "Biểu đồ dịch vụ")
                : "";
            const profileChartImg = hasChartData(profileReport?.chart)
                ? await generateChartImage(profileReport.chart, "Biểu đồ hồ sơ")
                : "";
            const financeChartImg = hasChartData(financialReport?.chart)
                ? await generateChartImage(
                    financialReport.chart,
                    "Biểu đồ tài chính",
                    "Tổng tiền",
                    (v) => v.toLocaleString() + " ₫"
                )
                : "";

            const profileRows = profileReport?.data || [];
            const tableHTML = `
                <h2 class="section-title">Danh sách hồ sơ</h2>
                <table>
                    <thead>
                        <tr>
                            <th style="width:60px">STT</th>
                            <th>Tên hồ sơ</th>
                            <th style="width:120px">Mã hồ sơ</th>
                            <th>Dịch vụ</th>
                            <th style="width:120px">Trạng thái</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${profileRows.length > 0
                            ? profileRows.map((item, idx) => `
                                <tr>
                                    <td>${idx + 1}</td>
                                    <td>${item.profileName || ""}</td>
                                    <td>${item.profileCode || "-"}</td>
                                    <td>${item.serviceName || ""}</td>
                                    <td>${item.status || ""}</td>
                                </tr>
                            `).join("")
                            : `<tr><td colspan="5" style="text-align:center;padding:20px;color:#6b7280">Không có dữ liệu trong khoảng thời gian này</td></tr>`
                        }
                    </tbody>
                </table>
            `;

            let bodyHTML = `<h1 class="heading">BÁO CÁO TỔNG QUAN</h1>`;
            if (appliedFilters.fromDate || appliedFilters.toDate) {
                bodyHTML += `
                    <div style="font-size:12px;color:#374151;margin-bottom:20px;text-align:center">
                        <b>Thời gian:</b> ${appliedFilters.fromDate || "..."} → ${appliedFilters.toDate || "..."}
                    </div>
                `;
            }

            bodyHTML += `
                <div class="report-section">
                    <div class="overview-summary-grid">
                        <div class="overview-summary-card">
                            <h3>Báo cáo dịch vụ</h3>
                            <p><b>Tổng số dịch vụ:</b> ${serviceReport?.summary?.totalServiceQuantity?.toLocaleString() || 0}</p>
                        </div>
                        <div class="overview-summary-card">
                            <h3>Báo cáo hồ sơ</h3>
                            <p><b>Tổng hồ sơ:</b> ${profileReport?.summary?.totalProfiles || 0}</p>
                            <p><b>Hoàn thành:</b> ${profileReport?.summary?.completedProfiles || 0}</p>
                            <p><b>Chưa hoàn thành:</b> ${profileReport?.summary?.unCompletedProfiles || 0}</p>
                        </div>
                        <div class="overview-summary-card">
                            <h3>Báo cáo tài chính</h3>
                            <p><b>Tổng tiền:</b> ${new Intl.NumberFormat("vi-VN").format(financialReport?.summary?.totalAmount || 0)} VNĐ</p>
                            <p><b>Đã chi:</b> ${new Intl.NumberFormat("vi-VN").format(financialReport?.summary?.paidAmount || 0)} VNĐ</p>
                            <p><b>Chưa thanh toán:</b> ${new Intl.NumberFormat("vi-VN").format(financialReport?.summary?.unpaidAmount || 0)} VNĐ</p>
                        </div>
                    </div>
                    <div class="overview-chart-grid">
                        <div class="overview-chart-card">
                            <h3>Dịch vụ</h3>
                            ${serviceChartImg ? `<img src="${serviceChartImg}" alt="Biểu đồ dịch vụ" />` : `<p style="text-align:center;color:#6b7280;padding:24px 0">Chưa có dữ liệu</p>`}
                        </div>
                        <div class="overview-chart-card">
                            <h3>Hồ sơ</h3>
                            ${profileChartImg ? `<img src="${profileChartImg}" alt="Biểu đồ hồ sơ" />` : `<p style="text-align:center;color:#6b7280;padding:24px 0">Chưa có dữ liệu</p>`}
                        </div>
                        <div class="overview-chart-card">
                            <h3>Tài chính</h3>
                            ${financeChartImg ? `<img src="${financeChartImg}" alt="Biểu đồ tài chính" />` : `<p style="text-align:center;color:#6b7280;padding:24px 0">Chưa có dữ liệu</p>`}
                        </div>
                    </div>
                </div>
                ${buildReportTableSection(tableHTML)}
            `;

            const html = buildPrintHTML(bodyHTML, "Báo cáo tổng quan");
            const watermarkBase64 = await toBase64FromPublic("/images/watermark-45.png");
            const BE_URL = getBackendUrl();

            const response = await fetch(`${BE_URL}/pdf/generate`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token_client}`,
                },
                body: JSON.stringify({
                    html,
                    profileName: "Báo cáo tổng quan",
                    watermarkBase64,
                    watermarkOpacity: 0.12,
                    options: { width: 1123, height: 794, deviceScaleFactor: 2 },
                    watermarkMode: "fixed",
                }),
            });

            if (!response.ok) throw new Error("Không thể xuất báo cáo tổng quan");

            const blob = await response.blob();
            const exportUrl = URL.createObjectURL(blob);
            const downloadLink = document.createElement("a");
            downloadLink.href = exportUrl;
            downloadLink.download = `Bao-cao-tong-quan-${Date.now()}.pdf`;
            downloadLink.click();
            URL.revokeObjectURL(exportUrl);

            toast.success("Xuất báo cáo thành công!");
        } catch (err) {
            console.error(err);
            toast.error(err.message || "Xuất PDF thất bại");
        } finally {
            setExporting(false);
        }
    };

    return (
        <OverviewTabPresenter
            loading={loading}
            exporting={exporting}
            fromDate={fromDate}
            toDate={toDate}
            setFromDate={setFromDate}
            setToDate={setToDate}
            handleApplyFilter={handleApplyFilter}
            handleExport={handleExport}
            serviceReport={serviceReport}
            profileReport={profileReport}
            financialReport={financialReport}
            hoveredCard={hoveredCard}
            setHoveredCard={setHoveredCard}
            navigate={navigate}
        />
    );
};

export default OverviewTab;
